var _a, _b;
import { jsx as _jsx } from "react/jsx-runtime";
import ReactDOM from 'react-dom/client';
import { getAppConfig, THEME_MEDIA_QUERY, THEME_STORAGE_KEY } from '@/lib/env';
import globalCss from '@/styles/globals.css';
import EmbedFixedAgentClient from './agent-client';
function getStoredThemeMode() {
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
            return stored;
        }
    }
    catch (_a) {
        // Ignored � storage might be unavailable in some embed contexts
    }
    return 'system';
}
function computeEffectiveTheme(mode) {
    if (mode === 'light' || mode === 'dark') {
        return mode;
    }
    try {
        return window.matchMedia && window.matchMedia(THEME_MEDIA_QUERY).matches ? 'dark' : 'light';
    }
    catch (_a) {
        return 'light';
    }
}
function applyThemeClass(target) {
    const mode = getStoredThemeMode();
    const theme = computeEffectiveTheme(mode);
    target.classList.remove('light', 'dark');
    target.classList.add(theme);
}
function subscribeToThemeChanges(target) {
    try {
        window.addEventListener('storage', (event) => {
            if (event.key === THEME_STORAGE_KEY) {
                applyThemeClass(target);
            }
        });
    }
    catch (_a) {
        // noop � storage events might be blocked
    }
    try {
        const media = window.matchMedia(THEME_MEDIA_QUERY);
        const handleChange = () => {
            if (getStoredThemeMode() === 'system') {
                applyThemeClass(target);
            }
        };
        if (typeof media.addEventListener === 'function') {
            media.addEventListener('change', handleChange);
        }
        else if (typeof media.addListener === 'function') {
            // Safari < 14 fallback
            media.addListener(handleChange);
        }
    }
    catch (_b) {
        // noop � matchMedia may not be available
    }
}
function injectAccentStyles(root, config) {
    const { accent, accentDark } = config;
    let themeStyle = root.querySelector('style[data-lk-theme-vars]');
    if (!themeStyle) {
        themeStyle = document.createElement('style');
        themeStyle.setAttribute('data-lk-theme-vars', 'true');
        root.appendChild(themeStyle);
    }
    const rules = [];
    if (accent) {
        rules.push(`:host { --primary: ${accent}; }`);
        rules.push(`:host .light { --primary: ${accent}; }`);
    }
    if (accentDark) {
        rules.push(`:host .dark { --primary: ${accentDark}; }`);
    }
    themeStyle.textContent = rules.join('\n');
}
function getAgentIdFromScript() {
    var _a;
    // Prefer the script element that loaded this bundle
    const current = (_a = document.currentScript) !== null && _a !== void 0 ? _a : undefined;
    const tryFrom = (el) => {
        var _a;
        if (!el)
            return undefined;
        // Common attribute names
        const attrId = el.getAttribute('data-agent-id') || undefined;
        if (attrId)
            return attrId;
        // Query params (support agentId)
        try {
            const u = new URL(el.src || '', window.location.origin);
            return (_a = u.searchParams.get('agentId')) !== null && _a !== void 0 ? _a : undefined;
        }
        catch (_b) {
            return undefined;
        }
    };
    let id = tryFrom(current);
    if (id)
        return id;
    // Fallback: search any <script> that looks like our bundle
    const scripts = Array.from(document.getElementsByTagName('script'));
    for (const s of scripts) {
        if ((s.src || '').includes('embed-popup.js') || (s.src || '').includes('widget.js')) {
            id = tryFrom(s);
            if (id)
                return id;
        }
    }
    return undefined;
}
function getStoreIdFromScript() {
    var _a;
    // Prefer the script element that loaded this bundle
    const current = (_a = document.currentScript) !== null && _a !== void 0 ? _a : undefined;
    const tryFrom = (el) => {
        var _a;
        if (!el)
            return undefined;
        // Common attribute names
        const attrId = el.getAttribute('data-store-id') || undefined;
        if (attrId)
            return attrId;
        // Query params (support storeId)
        try {
            const u = new URL(el.src || '', window.location.origin);
            return (_a = u.searchParams.get('storeId')) !== null && _a !== void 0 ? _a : undefined;
        }
        catch (_b) {
            return undefined;
        }
    };
    let id = tryFrom(current);
    if (id)
        return id;
    // Fallback: search any <script> that looks like our bundle
    const scripts = Array.from(document.getElementsByTagName('script'));
    for (const s of scripts) {
        if ((s.src || '').includes('embed-popup.js') || (s.src || '').includes('widget.js')) {
            id = tryFrom(s);
            if (id)
                return id;
        }
    }
    return undefined;
}
function setEmbedBaseFromScript() {
    var _a;
    const current = (_a = document.currentScript) !== null && _a !== void 0 ? _a : undefined;
    const findScript = () => {
        if (current)
            return current;
        const scripts = Array.from(document.getElementsByTagName('script'));
        return scripts.find((s) => (s.src || '').includes('embed-popup.js') || (s.src || '').includes('widget.js'));
    };
    const el = findScript();
    try {
        const origin = new URL((el === null || el === void 0 ? void 0 : el.src) || window.location.href).origin;
        // Expose a global for the bundle to discover its serving origin
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.__LK_EMBED_BASE__ = origin;
    }
    catch (_b) {
        // noop
    }
}
const wrapper = document.createElement('div');
wrapper.setAttribute('id', 'lk-embed-wrapper');
document.body.appendChild(wrapper);
// Use a shadow root so that any relevant css classes don't leak out and effect the broader page
const shadowRoot = wrapper.attachShadow({ mode: 'open' });
// Include all app styles into the shadow root
// Note: depending on css-loader configuration, the imported value can be a string
// or an object exposing toString(). Normalize to a string before injecting.
const styleTag = document.createElement('style');
try {
    const cssText = typeof globalCss === 'string'
        ? globalCss
        : ((_b = (_a = globalCss === null || globalCss === void 0 ? void 0 : globalCss.toString) === null || _a === void 0 ? void 0 : _a.call(globalCss)) !== null && _b !== void 0 ? _b : '');
    styleTag.textContent = cssText;
    shadowRoot.appendChild(styleTag);
}
catch (err) {
    console.error('Failed to inject embed styles into shadow root:', err);
}
const reactRoot = document.createElement('div');
reactRoot.classList.add('lk-embed-root');
shadowRoot.appendChild(reactRoot);
applyThemeClass(reactRoot);
subscribeToThemeChanges(reactRoot);
// Ensure embed base is set BEFORE any React render so assets resolve correctly on external hosts
setEmbedBaseFromScript();
getAppConfig(window.location.origin)
    .then((appConfig) => {
    injectAccentStyles(shadowRoot, appConfig);
    applyThemeClass(reactRoot);
    const root = ReactDOM.createRoot(reactRoot);
    const agentId = getAgentIdFromScript();
    const storeId = getStoreIdFromScript();
    root.render(_jsx(EmbedFixedAgentClient, { appConfig: appConfig, agentId: agentId, storeId: storeId }));
})
    .catch((err) => {
    console.error('Error loading livekit embed-popup app config:', err);
});
